/**
 * Image detail modal showing full preview and metadata.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Input, PencilIcon, TrashIcon } from '@/components/ui';
import { AISparkleIcon } from '@/components/ui/Icons';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useMediaImage,
  useUpdateImage,
  useDeleteImage,
  useSetImageCategory,
} from '@/hooks/queries/useMediaQueries';
import {
  findSpecByKey,
  usePlatformSpecs,
} from '@/hooks/queries/usePlatformSpecs';
import type { Platform } from '@/types/platformSpecs';

// Friendly platform labels. Defaults to the raw value for platforms not
// yet mapped, so adding a new one to PlatformEnum doesn't break this UI
// silently. Two label sets:
//   - PLATFORM_LABELS: full name, used in the Targeted-details panel
//     where we have room.
//   - PLATFORM_LABELS_SHORT: prefix-friendly, used in usage-history rows
//     where space is tight ("Google - Responsive Display Ad" reads better
//     than "Google Ads - Responsive Display Ad").
const PLATFORM_LABELS: Record<string, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta',
};
const PLATFORM_LABELS_SHORT: Record<string, string> = {
  google_ads: 'Google',
  meta_ads: 'Meta',
};
import type { MediaTag, MediaFolder, ImageUsage } from '@/types/media';
import { formatFileSize, getAspectRatioLabel } from '@/types/media';
import { toast } from '@/utils';

// Labels for ad types (when ad_type is available)
const AD_TYPE_LABELS: Record<string, string> = {
  'RESPONSIVE_DISPLAY_AD': 'Responsive Display Ad',
  'DEMAND_GEN_MULTI_ASSET_AD': 'Demand Gen Ad',
  'DEMAND_GEN_VIDEO_RESPONSIVE_AD': 'Demand Gen Video Ad',
  'DEMAND_GEN_CAROUSEL_AD': 'Demand Gen Carousel Ad',
};

// Labels for usage types (fallback when ad_type is not available)
const USAGE_TYPE_LABELS: Record<string, string> = {
  'ad': 'Ad',
  'asset_group': 'Performance Max',
  'ad_group': 'Ad Group',
  'campaign': 'Campaign'
};

// Labels for image field types
const FIELD_TYPE_LABELS: Record<string, string> = {
  'marketing_image': 'Landscape',
  'square_marketing_image': 'Square',
  'portrait_marketing_image': 'Portrait',
  'logo': 'Logo',
  'logo_image': 'Logo',
  'landscape_logo': 'Landscape Logo'
};

/**
 * Get a friendly display label for an image usage.
 * For ads, uses ad_type if available to distinguish between RDA, Demand Gen, etc.
 * Prefixes with the platform's short name (e.g. "Google - Responsive Display Ad")
 * so usage history rows make sense as platforms diversify (Google + Meta + …).
 */
function getUsageTypeLabel(usage: ImageUsage): string {
  const base =
    usage.usage_type === 'ad' && usage.ad_type
      ? AD_TYPE_LABELS[usage.ad_type] || usage.ad_type.replace(/_/g, ' ')
      : USAGE_TYPE_LABELS[usage.usage_type] || usage.usage_type;
  const platformShort = PLATFORM_LABELS_SHORT[usage.platform];
  return platformShort ? `${platformShort} - ${base}` : base;
}

/**
 * Get a friendly display label for an image field type.
 */
function getFieldTypeLabel(fieldType: string | null): string | null {
  if (!fieldType) return null;
  return FIELD_TYPE_LABELS[fieldType] || fieldType;
}

interface ImageDetailModalProps {
  imageId: string;
  isOpen: boolean;
  onClose: () => void;
  folders: MediaFolder[];
  tags: MediaTag[];
}

export function ImageDetailModal({
  imageId,
  isOpen,
  onClose,
  folders,
  tags,
}: ImageDetailModalProps) {
  const { data: image, isLoading } = useMediaImage(imageId);
  // Fetch the source MediaImage by id so we can render its filename in the
  // Targeted-details panel (instead of a UUID prefix). Only fires when the
  // image is Targeted and has a source_image_id; for other categories the
  // hook short-circuits via its `enabled` guard.
  const { data: sourceImage } = useMediaImage(image?.source_image_id ?? '');
  // Pull the placement spec so we can show the human-readable display_name
  // (e.g. "Square (1:1)") instead of the placement_key string.
  const { data: platformSpecs } = usePlatformSpecs(
    image?.platform ? { platform: image.platform as Platform } : undefined,
  );
  const placementSpec = image?.placement_key
    ? findSpecByKey(platformSpecs, image.placement_key)
    : null;
  const updateMutation = useUpdateImage();
  const deleteMutation = useDeleteImage();
  const setCategoryMutation = useSetImageCategory();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(false);
  const [usageHistoryExpanded, setUsageHistoryExpanded] = useState(false);
  const [editForm, setEditForm] = useState({
    altText: '',
    description: '',
    folderId: '',
    tagIds: [] as string[],
  });

  // Initialize form when image loads
  useEffect(() => {
    if (image) {
      setEditForm({
        altText: image.alt_text || '',
        description: image.description || '',
        folderId: image.folder_id || '',
        tagIds: image.tags.map((t) => t.id),
      });
    }
  }, [image]);

  const handleSave = async () => {
    if (!image) return;

    await updateMutation.mutateAsync({
      imageId: image.id,
      data: {
        alt_text: editForm.altText || undefined,
        description: editForm.description || undefined,
        folder_id: editForm.folderId || null,
        tag_ids: editForm.tagIds,
      },
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!image) return;
    await deleteMutation.mutateAsync(image.id);
    onClose();
  };

  // Promote/demote target: only meaningful for non-Targeted images. Targeted
  // is a derivative artifact and `targeted_image_service.set_category` rejects
  // it server-side; we hide the action here too.
  const categoryToggleTarget: 'reference' | 'approved' | null =
    image && image.category !== 'targeted'
      ? image.category === 'reference' ? 'approved' : 'reference'
      : null;

  const handleSetCategory = async () => {
    if (!image || !categoryToggleTarget) return;
    await setCategoryMutation.mutateAsync({
      imageId: image.id,
      category: categoryToggleTarget,
    });
    setShowCategoryConfirm(false);
  };

  // Targeted images are already-served ad creatives; refining them in
  // Creative Studio would orphan the rendered ad from its asset. Hide the
  // Edit-in-Studio affordance for that category.
  const canEditInStudio = !!image && image.category !== 'targeted';

  const handleEditInStudio = () => {
    if (!image) return;
    onClose();
    navigate(`/creative-studio?refine_from_library=${image.id}`);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {image?.original_filename || 'Image Details'}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Delete
                </Button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : image ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                  {/* Image preview */}
                  <div className="relative flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={image.signed_url}
                      alt={image.alt_text || image.original_filename}
                      className="max-w-full max-h-[400px] object-contain"
                    />
                    {canEditInStudio && (
                      <div className="absolute top-2 right-3">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleEditInStudio}
                          aria-label="Edit in Creative Studio"
                        >
                          <AISparkleIcon className="w-4 h-4 mr-1" />
                          Edit in Studio
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="relative space-y-4">
                    {!isEditing && (
                      <div className="absolute top-0 right-0">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                          aria-label="Edit metadata"
                        >
                          <PencilIcon className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    )}
                    {/* File info */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-2">File Information</h3>
                      <dl className="grid grid-cols-2 gap-2 text-sm">
                        <dt className="text-gray-500">Dimensions</dt>
                        <dd className="text-gray-900">
                          {image.width} x {image.height} ({getAspectRatioLabel(image.width, image.height)})
                        </dd>
                        <dt className="text-gray-500">Size</dt>
                        <dd className="text-gray-900">{formatFileSize(image.file_size_bytes)}</dd>
                        <dt className="text-gray-500">Type</dt>
                        <dd className="text-gray-900">{image.mime_type}</dd>
                        <dt className="text-gray-500">Uploaded</dt>
                        <dd className="text-gray-900">
                          {new Date(image.created_at).toLocaleDateString()}
                        </dd>
                      </dl>
                    </div>

                    {/* Editable fields */}
                    {isEditing ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Alt Text
                          </label>
                          <Input
                            value={editForm.altText}
                            onChange={(e) =>
                              setEditForm({ ...editForm, altText: e.target.value })
                            }
                            placeholder="Describe the image..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={editForm.description}
                            onChange={(e) =>
                              setEditForm({ ...editForm, description: e.target.value })
                            }
                            placeholder="Add a description..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Folder
                          </label>
                          <select
                            value={editForm.folderId}
                            onChange={(e) =>
                              setEditForm({ ...editForm, folderId: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="">Root (No folder)</option>
                            {folders.map((folder) => (
                              <option key={folder.id} value={folder.id}>
                                {folder.path}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tags
                          </label>
                          <p className="text-xs text-gray-500 mb-2">
                            Click available tags to associate with this image
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {tags.map((tag) => {
                              const isSelected = editForm.tagIds.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  onClick={() =>
                                    setEditForm({
                                      ...editForm,
                                      tagIds: isSelected
                                        ? editForm.tagIds.filter((id) => id !== tag.id)
                                        : [...editForm.tagIds, tag.id],
                                    })
                                  }
                                  className={`
                                    px-2.5 py-1 rounded-full text-xs font-medium transition-all border
                                    ${isSelected
                                      ? 'border-transparent'
                                      : 'border-gray-300 hover:border-gray-400'}
                                  `}
                                  style={isSelected ? {
                                    backgroundColor: `${tag.color}20`,
                                    color: tag.color,
                                  } : {
                                    backgroundColor: '#f3f4f6',
                                    color: '#9ca3af',
                                  }}
                                >
                                  {tag.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button onClick={handleSave} disabled={updateMutation.isPending}>
                            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button variant="secondary" onClick={() => setIsEditing(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Display mode */}
                        {(image.alt_text || image.description) && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Details</h3>
                            {image.alt_text && (
                              <p className="text-sm text-gray-600 mb-1">
                                <span className="font-medium">Alt:</span> {image.alt_text}
                              </p>
                            )}
                            {image.description && (
                              <p className="text-sm text-gray-600">{image.description}</p>
                            )}
                          </div>
                        )}

                        {/* Tags */}
                        {image.tags.length > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Tags</h3>
                            <div className="flex flex-wrap gap-2">
                              {image.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="px-2.5 py-1 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: `${tag.color}20`,
                                    color: tag.color,
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Folder */}
                        {image.folder_path && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Location</h3>
                            <p className="text-sm text-gray-600">{image.folder_path}</p>
                          </div>
                        )}

                        {/* Category */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 mb-2">Category</h3>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                image.category === 'targeted' ? 'warning'
                                  : image.category === 'reference' ? 'secondary'
                                  : 'success'
                              }
                            >
                              {image.category === 'targeted' ? 'Targeted'
                                : image.category === 'reference' ? 'Reference'
                                : 'Approved'}
                            </Badge>
                            {categoryToggleTarget && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowCategoryConfirm(true)}
                              >
                                {categoryToggleTarget === 'approved'
                                  ? 'Promote to Approved'
                                  : 'Demote to Reference'}
                              </Button>
                            )}
                          </div>
                          {image.category === 'targeted' && (
                            <p className="text-xs text-gray-500 mt-1.5">
                              Targeted images are derivative crops and cannot be re-categorized.
                            </p>
                          )}
                        </div>

                        {/* Targeted-only details: source, platform, placement, crop. */}
                        {image.category === 'targeted' && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Targeted details</h3>
                            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                              {image.platform && (
                                <>
                                  <dt className="text-gray-500">Platform</dt>
                                  <dd className="text-gray-900">
                                    {PLATFORM_LABELS[image.platform] ?? image.platform}
                                  </dd>
                                </>
                              )}
                              {image.placement_key && (
                                <>
                                  <dt className="text-gray-500">Placement</dt>
                                  <dd className="text-gray-900">
                                    {placementSpec?.display_name ?? image.placement_key}
                                    {placementSpec?.aspect_ratio_label && (
                                      <span className="text-gray-500 ml-1.5">
                                        ({placementSpec.aspect_ratio_label})
                                      </span>
                                    )}
                                  </dd>
                                </>
                              )}
                              {image.crop_source && (
                                <>
                                  <dt className="text-gray-500">Crop</dt>
                                  <dd className="text-gray-900">
                                    {image.crop_source === 'manual'
                                      ? 'Manually adjusted'
                                      : 'Auto-cropped'}
                                    {image.crop_box && (
                                      <span className="text-gray-500 ml-1.5">
                                        · {image.crop_box.w}×{image.crop_box.h} px
                                      </span>
                                    )}
                                  </dd>
                                </>
                              )}
                              {image.source_image_id && (
                                <>
                                  <dt className="text-gray-500">Source</dt>
                                  <dd className="text-gray-900">
                                    {sourceImage ? (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!navigator?.clipboard?.writeText) {
                                            toast.error('Clipboard not available.');
                                            return;
                                          }
                                          try {
                                            await navigator.clipboard.writeText(image.source_image_id!);
                                            toast.success('Source image ID copied');
                                          } catch {
                                            toast.error('Failed to copy.');
                                          }
                                        }}
                                        className="text-blue-600 hover:underline truncate inline-block max-w-full text-left"
                                        title={`Copy source image ID (${image.source_image_id})`}
                                      >
                                        {sourceImage.original_filename || sourceImage.filename}
                                      </button>
                                    ) : (
                                      // Fallback while the source is loading or
                                      // not accessible — surface the id so the
                                      // user has something to copy/search by.
                                      <span className="text-gray-500 font-mono text-xs">
                                        {image.source_image_id.slice(0, 8)}…
                                      </span>
                                    )}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Usage History */}
                        {image.usages.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-sm font-medium text-gray-900">
                                Usage History ({image.usages.length})
                              </h3>
                              <button
                                onClick={async () => {
                                  // Format all usages for copy
                                  const allUsagesText = image.usages.map(usage => {
                                    const typeLabel = getUsageTypeLabel(usage);
                                    const fieldTypeLabel = getFieldTypeLabel(usage.image_field_type);
                                    const linkedDate = usage.linked_at
                                      ? new Date(usage.linked_at).toLocaleDateString()
                                      : null;

                                    let displayLine = typeLabel;
                                    if (fieldTypeLabel) displayLine += ` - ${fieldTypeLabel}`;
                                    if (usage.campaign_name) displayLine += ` in ${usage.campaign_name}`;
                                    if (linkedDate) displayLine += ` - ${linkedDate}`;

                                    const idLines: string[] = [];
                                    if (usage.google_ads_account_id) {
                                      const accountId = usage.google_ads_account_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
                                      idLines.push(`Account:\t${accountId}`);
                                    }
                                    if (usage.campaign_id) idLines.push(`Campaign:\t${usage.campaign_id}`);
                                    if (usage.ad_group_id) idLines.push(`Ad Group:\t${usage.ad_group_id}`);
                                    if (usage.asset_group_id) idLines.push(`Asset Group:\t${usage.asset_group_id}`);
                                    if (usage.ad_id) idLines.push(`Ad:\t${usage.ad_id}`);

                                    return idLines.length > 0
                                      ? `${displayLine}\n${idLines.join('\n')}`
                                      : displayLine;
                                  }).join('\n\n');

                                  if (!navigator?.clipboard?.writeText) {
                                    toast.error('Clipboard is not available in this browser.');
                                    return;
                                  }
                                  try {
                                    await navigator.clipboard.writeText(allUsagesText);
                                    toast.success('All usage history copied to clipboard');
                                  } catch (error) {
                                    console.error('Failed to copy to clipboard', error);
                                    toast.error('Failed to copy to clipboard.');
                                  }
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 hover:bg-gray-100 rounded transition-colors"
                                title="Copy all usage history"
                              >
                                <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                Copy All
                              </button>
                            </div>
                            <ul className="space-y-1.5 text-sm">
                              {(usageHistoryExpanded ? image.usages : image.usages.slice(0, 5)).map((usage) => {
                                const typeLabel = getUsageTypeLabel(usage);
                                const fieldTypeLabel = getFieldTypeLabel(usage.image_field_type);

                                // Format date
                                const linkedDate = usage.linked_at
                                  ? new Date(usage.linked_at).toLocaleDateString()
                                  : null;

                                // Build display line for copy text
                                let displayLine = typeLabel;
                                if (fieldTypeLabel) displayLine += ` - ${fieldTypeLabel}`;
                                if (usage.campaign_name) displayLine += ` in ${usage.campaign_name}`;
                                if (linkedDate) displayLine += ` - ${linkedDate}`;

                                // Build tooltip data (hierarchical order)
                                const tooltipItems: { label: string; value: string }[] = [];
                                if (usage.google_ads_account_id) {
                                  // Format account ID with dashes (e.g., 554-320-0460)
                                  const accountId = usage.google_ads_account_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
                                  tooltipItems.push({ label: 'Account', value: accountId });
                                }
                                if (usage.campaign_id) tooltipItems.push({ label: 'Campaign', value: usage.campaign_id });
                                if (usage.ad_group_id) tooltipItems.push({ label: 'Ad Group', value: usage.ad_group_id });
                                if (usage.asset_group_id) tooltipItems.push({ label: 'Asset Group', value: usage.asset_group_id });
                                if (usage.ad_id) tooltipItems.push({ label: 'Ad', value: usage.ad_id });

                                // Plain text for copy (tab-aligned)
                                const tooltipText = tooltipItems.map(item => `${item.label}:\t${item.value}`).join('\n');

                                // Full copy text includes display line + IDs
                                const copyText = tooltipItems.length > 0
                                  ? `${displayLine}\n${tooltipText}`
                                  : displayLine;

                                const handleCopyIds = async () => {
                                  if (!navigator?.clipboard?.writeText) {
                                    toast.error('Clipboard is not available in this browser.');
                                    return;
                                  }
                                  try {
                                    await navigator.clipboard.writeText(copyText);
                                    toast.success('Copied to clipboard');
                                  } catch (error) {
                                    console.error('Failed to copy to clipboard', error);
                                    toast.error('Failed to copy to clipboard.');
                                  }
                                };

                                return (
                                  <li
                                    key={usage.id}
                                    className="text-gray-600 flex items-center group relative"
                                  >
                                    {/* Copy button on the left with fixed width for alignment */}
                                    <div className="w-5 flex-shrink-0">
                                      {tooltipItems.length > 0 && (
                                        <button
                                          onClick={handleCopyIds}
                                          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-100 rounded"
                                          title="Copy to clipboard"
                                          aria-label="Copy usage IDs to clipboard"
                                        >
                                          <ClipboardDocumentIcon className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                                        </button>
                                      )}
                                    </div>
                                    <span className="font-medium text-gray-700">{typeLabel}</span>
                                    {fieldTypeLabel && (
                                      <>
                                        <span className="text-gray-300 mx-1.5">·</span>
                                        <span className="text-gray-500">{fieldTypeLabel}</span>
                                      </>
                                    )}
                                    {usage.campaign_name && (
                                      <>
                                        <span className="text-gray-300 mx-1.5">·</span>
                                        <span>{usage.campaign_name}</span>
                                      </>
                                    )}
                                    {linkedDate && (
                                      <>
                                        <span className="text-gray-300 mx-1.5">·</span>
                                        <span className="text-gray-400 text-xs">{linkedDate}</span>
                                      </>
                                    )}
                                    {/* Custom tooltip with smooth transitions */}
                                    {tooltipItems.length > 0 && (
                                      <div className="absolute left-0 bottom-full mb-1 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-75 group-hover:delay-0">
                                        <div className="bg-gray-900 text-white text-xs rounded px-2 py-1.5 shadow-lg">
                                          <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
                                            {tooltipItems.map((item, idx) => (
                                              <React.Fragment key={idx}>
                                                <span className="text-gray-400">{item.label}</span>
                                                <span className="font-mono">{item.value}</span>
                                              </React.Fragment>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                            {image.usages.length > 5 && (
                              <button
                                onClick={() => setUsageHistoryExpanded(!usageHistoryExpanded)}
                                className="mt-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                              >
                                {usageHistoryExpanded ? (
                                  <>
                                    <ChevronUpIcon className="w-4 h-4" />
                                    Show less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="w-4 h-4" />
                                    Show {image.usages.length - 5} more
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Image not found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDialog
        isOpen={showCategoryConfirm}
        onClose={() => setShowCategoryConfirm(false)}
        onConfirm={handleSetCategory}
        title={
          categoryToggleTarget === 'approved'
            ? 'Promote to Approved'
            : 'Demote to Reference'
        }
        message={
          categoryToggleTarget === 'approved'
            ? 'Approved images are eligible for ad attachment and visible in the image selector.'
            : 'Reference images are studio inputs only — they will be hidden from the image selector.'
        }
        confirmText={categoryToggleTarget === 'approved' ? 'Promote' : 'Demote'}
        variant={categoryToggleTarget === 'approved' ? 'info' : 'warning'}
        isLoading={setCategoryMutation.isPending}
      />
    </>
  );
}
